import React from "react";

/* DataTable — column-driven table. Header 12px grey medium; rows 14px.
   columns: [{key,label,align?,width?,render?}]. Numbers right-align.
   Optional row striping, hover, click. */
export function DataTable({
  columns = [],
  rows = [],
  rowKey = "id",
  onRowClick,
  striped = false,
  emptyText = "No records yet.",
  highlightRowKey,
  rowBackground,
  rowTestId,
  footer,
  style,
  ...rest
}) {
  return (
    <div style={{ width: "100%", overflowX: "auto", ...style }} {...rest}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-sans)" }}>
        <thead>
          <tr>
            {columns.map((c, colIndex) => (
              <th
                key={c.key}
                style={{
                  textAlign: c.align || (colIndex > 0 && c.numeric ? "right" : "left"),
                  padding: "10px 16px",
                  fontWeight: 500,
                  fontSize: 12,
                  color: "var(--text-muted)",
                  borderBottom: "1px solid var(--divider)",
                  whiteSpace: "nowrap",
                  width: c.width,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ padding: "28px 16px", textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>
                {emptyText}
              </td>
            </tr>
          ) : (
            rows.map((row, ri) => (
              <Tr
                key={row[rowKey] ?? ri}
                row={row}
                columns={columns}
                striped={striped && ri % 2 === 1}
                onRowClick={onRowClick}
                highlighted={highlightRowKey != null && row[rowKey] === highlightRowKey}
                restingBackground={rowBackground ? rowBackground(row) : undefined}
                testId={rowTestId ? rowTestId(row) : undefined}
              />
            ))
          )}
        </tbody>
        {footer ? <tfoot>{footer}</tfoot> : null}
      </table>
    </div>
  );
}

function Tr({ row, columns, striped, onRowClick, highlighted, restingBackground, testId }) {
  const [hover, setHover] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (highlighted && ref.current && typeof ref.current.scrollIntoView === "function") {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlighted]);
  const background = highlighted
    ? "var(--highlight-bg, #FFF7CC)"
    : hover && onRowClick
    ? "var(--grey-bg)"
    : restingBackground
    ? restingBackground
    : striped
    ? "var(--surface-sunken)"
    : "transparent";
  return (
    <tr
      ref={ref}
      data-testid={testId}
      data-highlighted={highlighted || undefined}
      onClick={() => onRowClick && onRowClick(row)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background,
        cursor: onRowClick ? "pointer" : "default",
        transition: "background var(--dur) var(--ease)",
      }}
    >
      {columns.map((c, colIndex) => (
        <td
          key={c.key}
          className={c.numeric ? "tnum" : undefined}
          data-testid={c.cellTestId ? c.cellTestId(row) : undefined}
          style={{
            textAlign: c.align || (colIndex > 0 && c.numeric ? "right" : "left"),
            padding: "13px 16px",
            fontSize: 14,
            fontWeight: c.strong ? 700 : 500,
            color: c.strong ? "var(--text-strong)" : "var(--text-body)",
            borderBottom: "1px solid var(--divider)",
            whiteSpace: c.wrap ? "normal" : "nowrap",
            fontVariantNumeric: c.numeric ? "tabular-nums" : undefined,
          }}
        >
          {c.render ? c.render(row[c.key], row) : row[c.key]}
        </td>
      ))}
    </tr>
  );
}

export default DataTable;
